import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { Add, Bed, Chair, DirectionsBoat, Remove } from '@mui/icons-material';

import { AccommodationOption, OfferOptions, TicketTypeOption } from '../queries';
import { formatMoney } from '../money';
import { prettifyCode } from '../format';
import {
  accommodationSelectionStatus,
  coveredPassengers,
} from '../offerOptionsSelection';

interface Props {
  options: OfferOptions;
  /** Cuántos pasajeros hay para acomodar. */
  passengers: number;
  /**
   * La travesía exige plaza asignada y hay plazas publicadas (lo resuelve el
   * modal). Solo cambia el cartel de "opcional" a "falta elegir": si la
   * selección vale la decide el operador al cotizarla; el aviso de exigencia
   * en sí lo pinta el modal, fuera del picker, porque también aplica cuando
   * no hay plazas publicadas.
   */
  accommodationRequired?: boolean;
  /** Una entrada POR PLAZA: repetir el id reserva varias plazas. */
  accommodationIds: string[];
  onAccommodationIdsChange: (ids: string[]) => void;
  ticketTypeId?: string;
  onTicketTypeChange: (id: string) => void;
}

/** Texto según cómo se comparan las tarifas — lo decidió el backend. */
const FARE_HINT_KEY = {
  different: 'travelServices.ferry.fareOptional',
  same: 'travelServices.ferry.fareSamePrice',
  unknown: 'travelServices.ferry.farePriceUnknown',
} as const;

const KIND_ICONS = {
  cabin: Bed,
  seat: Chair,
  deck: DirectionsBoat,
  other: DirectionsBoat,
} as const;

/** Plazas ya elegidas de una acomodación (el id se repite una vez por plaza). */
function countFor(ids: string[], id: string): number {
  return ids.filter((entry) => entry === id).length;
}

/**
 * Elección de acomodación y tarifa de una travesía. En ferry la oferta no es un
 * producto cerrado: se puede elegir dónde viaja cada pasajero (butaca,
 * camarote, cubierta) y con qué condiciones. Se cuentan plazas, no items (una
 * entrada por plaza). Lo único que bloquea es lo que el contrato del hub
 * garantiza: en las travesías que exigen plaza asignada, todos cubiertos.
 *
 * Todo lo monetario llega resuelto del backend: `supplement.final` de cada
 * opción (con markup, plaza incluida en 0, no comparable en null) y
 * `farePriceComparison`. Acá solo se formatea.
 */
export function FerryOptionsPicker({
  options,
  passengers,
  accommodationRequired = false,
  accommodationIds,
  onAccommodationIdsChange,
  ticketTypeId,
  onTicketTypeChange,
}: Props) {
  const { t } = useTranslation();

  // En roundtrip cada tramo ofrece acomodaciones distintas.
  const byLeg = useMemo(() => {
    const groups = new Map<string, AccommodationOption[]>();
    for (const option of options.accommodations) {
      const leg = option.leg ?? 'outbound';
      groups.set(leg, [...(groups.get(leg) ?? []), option]);
    }
    return [...groups.entries()];
  }, [options.accommodations]);

  /**
   * Los deltas de tarifa se pintan solo cuando el backend afirmó que las
   * tarifas se distinguen por precio: con `same` rotular cada una "Incluida"
   * sugiere una diferencia que no existe, y con `unknown` no hay base.
   */
  const farePriceComparison = options.farePriceComparison ?? 'unknown';
  const showFareDeltas = farePriceComparison === 'different';
  /**
   * El backend no tuvo el contexto comercial de la oferta: no hay adicionales
   * cotizados (todos `null`) y se avisa, en vez de dejar que "no aparece
   * ningún precio" pase por "todo incluido".
   */
  const pricingUnavailable = options.pricingState === 'unavailable';

  const seatsTaken = accommodationIds.length;
  // Cobertura para el CARTEL, con los datos del hub (`coversPassengers` por
  // plaza, `passengersToCover` con infantes descontados). Si la selección vale
  // lo dice el operador al cotizarla, no este número.
  const passengersToCover = options.passengersToCover ?? passengers;
  const covered = coveredPassengers({
    selectedIds: accommodationIds,
    accommodations: options.accommodations,
  });
  const missing = Math.max(passengersToCover - covered, 0);
  const selectionStatus = accommodationSelectionStatus({
    selectedCount: seatsTaken,
    covered,
    passengersToCover,
  });

  const changeSeats = (option: AccommodationOption, delta: number) => {
    const current = countFor(accommodationIds, option.id);
    const next = current + delta;
    if (next < 0) return;
    // Nunca más plazas que pasajeros, ni más de las que el operador publica.
    if (delta > 0 && seatsTaken >= passengers) return;
    if (
      delta > 0 &&
      typeof option.availability === 'number' &&
      next > option.availability
    ) {
      return;
    }

    const rest = accommodationIds.filter((id) => id !== option.id);
    onAccommodationIdsChange([...rest, ...Array.from({ length: next }, () => option.id)]);
  };

  const accommodationLabel = (option: AccommodationOption) =>
    [
      option.name,
      option.capacity ? t('travelServices.ferry.berths', { count: option.capacity }) : '',
      option.shared ? t('travelServices.ferry.shared') : '',
      option.gender ?? '',
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {options.message && <Alert severity="info">{options.message}</Alert>}
      {pricingUnavailable && (
        <Alert severity="warning">
          {t('travelServices.ferry.pricingUnavailable')}
        </Alert>
      )}

      {options.accommodations.length > 0 && (
        <Box>
          <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2">
              {t('travelServices.ferry.accommodation')}
            </Typography>
            {/* Tres estados descriptivos: no elegir es válido cuando la plaza es
                opcional (antes salía "faltan N" en rojo desde el arranque) y
                es lo que falta hacer cuando la travesía la exige; "faltan
                cubrir N" es informativo — si la selección no vale lo dice el
                operador al cotizarla, con su motivo, debajo del picker. */}
            {selectionStatus === 'untouched' ? (
              <Typography
                variant="caption"
                color={accommodationRequired ? 'warning.main' : 'text.secondary'}
              >
                {t(
                  accommodationRequired
                    ? 'travelServices.ferry.seatsRequired'
                    : 'travelServices.ferry.seatsOptional',
                )}
              </Typography>
            ) : selectionStatus === 'partial' ? (
              <Typography
                variant="caption"
                color={accommodationRequired ? 'warning.main' : 'text.secondary'}
              >
                {t('travelServices.ferry.seatsPartial', { count: missing })}
              </Typography>
            ) : (
              <Typography variant="caption" color="success.main">
                {t('travelServices.ferry.seatsCovered', { count: passengersToCover })}
              </Typography>
            )}
          </Box>

          {byLeg.map(([leg, accommodations]) => (
            <Box key={leg} mt={1}>
              {byLeg.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  {t(
                    leg === 'return'
                      ? 'travelServices.booking.return'
                      : 'travelServices.booking.outbound',
                  )}
                </Typography>
              )}
              <Stack spacing={0.5} mt={0.5}>
                {accommodations.map((option) => {
                  const Icon = KIND_ICONS[option.kind] ?? DirectionsBoat;
                  const seats = countFor(accommodationIds, option.id);
                  return (
                    <Box
                      key={`${leg}-${option.id}`}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      px={1.5}
                      py={1}
                      borderRadius={1}
                      bgcolor={seats > 0 ? 'action.selected' : 'grey.100'}
                    >
                      <Icon fontSize="small" color="action" />
                      <Box flex={1} minWidth={0}>
                        <Typography variant="body2">
                          {accommodationLabel(option)}
                        </Typography>
                        {/* `default` lo dice el hub: es la que queda si no se
                            elige nada (y elegir otra la reemplaza). */}
                        {(option.default || option.includedInPrice) && (
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={t(
                              option.default
                                ? 'travelServices.ferry.defaultAccommodation'
                                : 'travelServices.ferry.includedInPrice',
                            )}
                          />
                        )}
                      </Box>
                      {/* Se pinta lo que el backend dice que suma (con
                          markup); la plaza incluida ya viene en 0 y la no
                          comparable en null. El `price` crudo no se mira. */}
                      {option.supplement && option.supplement.final.amountMinor > 0 && (
                        <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                          {t('travelServices.ferry.extraPrice', {
                            price: formatMoney(option.supplement.final),
                          })}
                        </Typography>
                      )}
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <IconButton
                          size="small"
                          disabled={seats === 0}
                          onClick={() => changeSeats(option, -1)}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" minWidth={16} textAlign="center">
                          {seats}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={seatsTaken >= passengers}
                          onClick={() => changeSeats(option, 1)}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      {options.ticketTypes.length > 0 && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2">
              {t('travelServices.ferry.ticketType')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {t(FARE_HINT_KEY[farePriceComparison])}
            </Typography>
            <RadioGroup
              value={ticketTypeId ?? ''}
              onChange={(event) => onTicketTypeChange(event.target.value)}
            >
              <Stack spacing={1}>
                {options.ticketTypes.map((option: TicketTypeOption) => {
                  const delta = showFareDeltas ? option.supplement?.final ?? null : null;
                  return (
                    <Paper
                      key={option.id}
                      variant="outlined"
                      sx={(theme) => ({
                        px: 1.5,
                        py: 0.5,
                        borderColor:
                          option.id === ticketTypeId ? 'success.main' : 'divider',
                        // Tinte, no verde pleno: `success.light` en este theme
                        // es casi el mismo verde que `main` y se comía el texto.
                        bgcolor:
                          option.id === ticketTypeId
                            ? alpha(theme.palette.success.main, 0.1)
                            : 'transparent',
                      })}
                    >
                      <FormControlLabel
                        value={option.id}
                        control={<Radio size="small" color="success" />}
                        sx={{
                          m: 0,
                          width: '100%',
                          // El theme pone `lowercase` a TODA label que siga a un
                          // Radio; acá el texto es del operador y va como viene.
                          '& .MuiRadio-root + .MuiFormControlLabel-label': {
                            textTransform: 'none',
                          },
                        }}
                        label={
                          <Box
                            display="flex"
                            alignItems="baseline"
                            gap={1}
                            width="100%"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">
                              {prettifyCode(option.name)}
                            </Typography>
                            {delta && (
                              <Typography
                                variant="body2"
                                color={delta.amountMinor === 0 ? 'success.main' : 'text.primary'}
                                whiteSpace="nowrap"
                                fontWeight={600}
                              >
                                {delta.amountMinor === 0
                                  ? t('travelServices.ferry.fareIncluded')
                                  : t('travelServices.ferry.extraPrice', {
                                    price: formatMoney(delta),
                                  })}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Paper>
                  );
                })}
              </Stack>
            </RadioGroup>
            {!pricingUnavailable && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {t('travelServices.ferry.fareFinalHint')}
              </Typography>
            )}
            {/* Condiciones de la tarifa elegida: es la letra chica que se le
                transmite al pasajero antes de confirmar. */}
            {options.ticketTypes
              .filter((option) => option.id === ticketTypeId)
              .map((option) => (
                <Box key={option.id} mt={1} px={1.5} py={1} bgcolor="grey.100" borderRadius={1}>
                  {option.description && (
                    <Typography variant="caption" display="block">
                      {option.description}
                    </Typography>
                  )}
                  {option.conditions?.map((condition, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      • {condition}
                    </Typography>
                  ))}
                </Box>
              ))}
          </Box>
        </>
      )}
    </Box>
  );
}
